import { DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import PageHeaderEditEntityButton, {
    PageHeaderEditEntityButtonMenuItem,
} from "../../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import React from "react";
import { useParams } from "react-router";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { useFrameworkTemplateOverview } from "./FrameworkTemplateOverview.hooks.ts";
import FrameworkTemplateControl from "./frameworkTemplateControl/FrameworkTemplateControl.tsx";
import CreateEditTemplateDialog from "../createEditTemplateDialog/CreateEditTemplateDialog.tsx";
import { FrameworkTemplateSaveModel } from "../createEditTemplateDialog/CreateEditTemplateDialog.types.ts";
import Grid from "@mui/material/Grid2";
import { formatTemplateRequirementPlansToString } from "../templates/Templates.hooks.ts";
import FrameworkTemplateStatusChip from "../FrameworkTemplateStatusChip.tsx";
import ToggleFrameworkTemplateStatusDialog from "./toggleFrameworkTemplateStatusDialog/ToggleFrameworkTemplateStatusDialog.tsx";
import { getFrameworkImage } from "../../framework/frameworks/Frameworks.types.ts";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTile, { useTopBarTileSize } from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import TopPaperComplianceAreasWrapper from "@common/components/TopPaperComplianceAreasWrapper";

export interface FrameworkTemplateOverviewProps {}

function FrameworkTemplateOverview() {
    const urlProvider = useUrlProvider();
    const { id } = useParams<{ id: string }>();
    const { isLoading, data, refetch, showEdit, setShowEdit, showToggleIsPublishedDialog, setShowToggleIsPublishedDialog } = useFrameworkTemplateOverview();

    let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [
        { menuItem: "Manage categories", href: urlProvider.getFrameworkTemplateCategoriesUrl(id!), onClick: () => {} },
    ];
    if (data) {
        menuItems.unshift({
            menuItem: data.isPublished ? "Deactivate framework" : "Activate framework",
            onClick: () => setShowToggleIsPublishedDialog(data!),
        });
        menuItems.unshift({ menuItem: "Edit", onClick: () => setShowEdit(true) });
    }

    const tileSize = useTopBarTileSize();

    return (
        <>
            <DotLegalPageHeader
                breadCrumbs={[
                    { name: "Settings", link: urlProvider.getSettingsUrl() },
                    { name: "Framework templates", link: urlProvider.getFrameworkTemplatesUrl() },
                    { name: data?.name ?? "", tooltip: data?.description ?? undefined },
                ]}
                rightComponent={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <PageHeaderEditEntityButton menuItems={menuItems} />
                    </Box>
                }
            />

            <EntityDetailTopBar>
                <TopBarRow>
                    <Grid size={tileSize} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {!isLoading && <Box component={"img"} sx={{ maxWidth: 90 }} src={getFrameworkImage(data!.name)} />}
                    </Grid>
                    <TopBarTile label={"Compliance areas"}>
                        <TopPaperComplianceAreasWrapper isLoading={isLoading} complianceAreas={data?.complianceAreas.map((x) => x.name) ?? []} />
                    </TopBarTile>
                    <TopBarTextTile label={"Status"} value={<FrameworkTemplateStatusChip isPublished={data?.isPublished ?? false} />} isLoading={isLoading} />
                    <TopBarTextTile
                        label={"Required plans"}
                        value={formatTemplateRequirementPlansToString(data?.templatePlanRequirements ?? [])}
                        isLoading={isLoading}
                    />
                </TopBarRow>
            </EntityDetailTopBar>

            <FrameworkTemplateControl controls={data?.controls} isLoading={isLoading} refetch={refetch} frameworkTemplateId={id!} />

            {showEdit && (
                <CreateEditTemplateDialog
                    onCloseDialog={() => {
                        setShowEdit(false);
                    }}
                    onTemplateEdited={() => {
                        setShowEdit(false);
                        refetch();
                    }}
                    editModel={{
                        templateId: id!,
                        model: new FrameworkTemplateSaveModel(
                            data!.complianceAreas.map((x) => x.id),
                            data!.templatePlanRequirements,
                            data!.languages
                        ),
                    }}
                />
            )}

            {showToggleIsPublishedDialog && (
                <ToggleFrameworkTemplateStatusDialog
                    isPublished={showToggleIsPublishedDialog.isPublished}
                    templateId={showToggleIsPublishedDialog.id}
                    templateName={showToggleIsPublishedDialog.name}
                    onCloseDialog={() => setShowToggleIsPublishedDialog(undefined)}
                    onToggleSuccess={() => {
                        refetch();
                        setShowToggleIsPublishedDialog(undefined);
                    }}
                />
            )}
        </>
    );
}

export default FrameworkTemplateOverview;
